import { AdminApiError, request } from '../api/client.js'
import type {
  CreateHrUserBody,
  HrCohortAccess,
  HrUser,
  HrUserListResponse,
  ListHrUsersParams,
  UpdateHrUserBody,
} from './types.js'

const path = (clientId: string, userId?: string): string =>
  `/coach/admin/clients/${clientId}/hr-users${userId ? `/${userId}` : ''}`

export const adminHrUsersApi = {
  list(params: ListHrUsersParams = {}): Promise<HrUserListResponse> {
    const qs = new URLSearchParams()
    if (params.clientId) qs.set('clientId', params.clientId)
    if (params.search) qs.set('search', params.search)
    if (params.limit !== undefined) qs.set('limit', String(params.limit))
    if (params.offset !== undefined) qs.set('offset', String(params.offset))
    const suffix = qs.size > 0 ? `?${qs.toString()}` : ''
    return request<HrUserListResponse>(`/coach/admin/hr-users${suffix}`, {
      method: 'GET',
    })
  },
  create(clientId: string, body: CreateHrUserBody): Promise<HrUser> {
    return request<HrUser>(path(clientId), {
      method: 'POST',
      body: JSON.stringify(body),
    })
  },
  get(clientId: string, userId: string): Promise<HrUser> {
    return request<HrUser>(path(clientId, userId), { method: 'GET' })
  },
  update(
    clientId: string,
    userId: string,
    body: UpdateHrUserBody,
  ): Promise<HrUser> {
    return request<HrUser>(path(clientId, userId), {
      method: 'PATCH',
      body: JSON.stringify(body),
    })
  },
  resendInvite(clientId: string, userId: string): Promise<HrUser> {
    return request<HrUser>(`${path(clientId, userId)}/invite/resend`, {
      method: 'POST',
    })
  },
  getCohortAccess(clientId: string, userId: string): Promise<HrCohortAccess> {
    return request<HrCohortAccess>(
      `/coach/admin/clients/${clientId}/users/${userId}/cohort-access`,
      { method: 'GET' },
    )
  },
  setCohortAccess(
    clientId: string,
    userId: string,
    cohortIds: string[],
  ): Promise<HrCohortAccess> {
    return request<HrCohortAccess>(
      `/coach/admin/clients/${clientId}/users/${userId}/cohort-access`,
      { method: 'PUT', body: JSON.stringify({ cohortIds }) },
    )
  },
  grantCohortAccess(
    clientId: string,
    userId: string,
    cohortId: string,
  ): Promise<HrCohortAccess> {
    return request<HrCohortAccess>(
      `/coach/admin/clients/${clientId}/users/${userId}/cohort-access/${cohortId}`,
      { method: 'PUT' },
    )
  },
  revokeCohortAccess(
    clientId: string,
    userId: string,
    cohortId: string,
  ): Promise<HrCohortAccess> {
    return request<HrCohortAccess>(
      `/coach/admin/clients/${clientId}/users/${userId}/cohort-access/${cohortId}`,
      { method: 'DELETE' },
    )
  },
}

export function messageForHrUserError(error: unknown): string {
  if (error instanceof AdminApiError) {
    switch (error.detail) {
      case 'email_already_registered':
        return 'That email address is already registered.'
      case 'cohort_not_found':
        return 'One of the selected cohorts no longer exists.'
      case 'hr_all_cohort_access_locked':
        return 'This HR user has all-cohort access. Turn it off before removing individual cohorts.'
      case 'hr_user_not_found':
        return 'That HR user no longer exists.'
      case 'admin_required':
        return 'You do not have permission to manage HR users.'
      case 'invite_not_available':
        return 'This HR user cannot be invited because the identity has already been activated.'
      case 'rate_limited':
        return 'Too many invitations were sent to this HR user. Wait a few minutes and try again.'
      default:
        return error.message
    }
  }
  return error instanceof Error ? error.message : 'Request failed'
}
